import React from "react";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Mail, Phone, Calendar, MessageSquare } from "lucide-react";

export default function AdminContacts() {
  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => base44.entities.Contact.list('-created_date')
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <p className="text-center">Chargement des contacts...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">
            Contacts Reçus
          </h1>
          <p className="text-slate-600">
            {contacts.length} contact{contacts.length > 1 ? 's' : ''} au total
          </p>
        </div>

        {contacts.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-slate-600">Aucun contact reçu pour le moment</p>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-6">
          {contacts.map((contact) => (
            <Card key={contact.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-2xl">{contact.name}</CardTitle>
                    <p className="text-sm text-slate-600 mt-1 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {new Date(contact.created_date).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <Badge className={
                    contact.interest === "wefi" 
                      ? "bg-blue-600 text-white" 
                      : "bg-green-600 text-white"
                  }>
                    {contact.interest === "wefi" ? "WeFi" : "BitNest"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-600">Email</p>
                      <a href={`mailto:${contact.email}`} className="font-semibold text-blue-600 hover:underline">
                        {contact.email}
                      </a>
                    </div>
                  </div>

                  {contact.phone && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <Phone className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-600">Téléphone</p>
                        <a href={`tel:${contact.phone}`} className="font-semibold text-green-600 hover:underline">
                          {contact.phone}
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {contact.message && (
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <div className="flex items-start gap-2 mb-2">
                      <MessageSquare className="w-5 h-5 text-slate-600 mt-0.5" />
                      <p className="font-semibold text-slate-900">Message:</p>
                    </div>
                    <p className="text-slate-700 whitespace-pre-wrap pl-7">
                      {contact.message}
                    </p>
                  </div>
                )}

                <div className="pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-600">
                    <strong>Lien de parrainage à partager:</strong>
                  </p>
                  <a 
                    href={contact.interest === "wefi" 
                      ? "https://app.wefi.co/register?ref=p9ybxqycq2" 
                      : "https://bitnest.me/X9S6L"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline font-mono text-sm break-all"
                  >
                    {contact.interest === "wefi" 
                      ? "https://app.wefi.co/register?ref=p9ybxqycq2" 
                      : "https://bitnest.me/X9S6L"
                    }
                  </a>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
